import React, { useState } from "react";
import { Button, Slider } from "antd";
import { find, isNil, map } from "lodash";
import { DropdownInput } from "components";
import { EMPTY } from "constants/common";
import { AICC_PROMPT_TEMPERATURE } from "constants/aiccPrompt";
import { AiccPromptModel, AiccPromptSharedModel } from "types/model/aiccPrompt";
import ModelCostModal from "./ModelCostModal";
import { appliesTemperature } from "./appliesTemperature";
import { confirmDeprecatedModel, confirmSharedSettingChange } from "../confirmations";
import {
  ChangeSettingKey,
  EFFORT_LABEL,
  MODEL_COST_BUTTON,
  MODEL_DEPRECATED_SUFFIX,
  TEMPERATURE_CREATIVE,
  TEMPERATURE_LABEL,
  TEMPERATURE_STRICT,
} from "../configs";
import styles from "./ModelSettings.module.scss";

interface Props {
  models: AiccPromptModel[];
  modelId?: string;
  temperature?: number;
  reasoningEffort?: string;
  sharesModelWith: AiccPromptSharedModel[];
  onModelChange: (modelId: string) => void;
  onTemperatureChange: (temperature: number) => void;
  onEffortChange: (effort: string) => void;
}

function ModelSettings({
  models,
  modelId,
  temperature,
  reasoningEffort,
  sharesModelWith,
  onModelChange,
  onTemperatureChange,
  onEffortChange,
}: Props) {
  const [isCostVisible, setIsCostVisible] = useState(false);
  const [draggedTemperature, setDraggedTemperature] = useState<number>();
  const selected = find(models, { id: modelId });
  const efforts = selected?.efforts ?? [];
  const showsTemperature = appliesTemperature(reasoningEffort);

  const applyIfConfirmed = (setting: ChangeSettingKey, currentValue: string, nextValue: string, apply: () => void) => {
    if (!sharesModelWith.length) {
      apply();
      return Promise.resolve(true);
    }

    return confirmSharedSettingChange({ setting, currentValue, nextValue, sharesModelWith }).then((confirmed) => {
      if (confirmed) {
        apply();
      }
      return confirmed;
    });
  };

  const handleModelChange = (nextId: string) => {
    if (nextId === modelId) {
      return;
    }

    const next = find(models, { id: nextId });
    const applyShared = () =>
      applyIfConfirmed("model", selected?.name ?? modelId ?? EMPTY, next?.name ?? nextId, () => onModelChange(nextId));

    if (next?.isDeprecated) {
      confirmDeprecatedModel(next.name, next.deprecationNote, next.shutdownAt, next.recommendedAlternatives).then(
        (accepted) => {
          if (accepted) {
            applyShared();
          }
        }
      );
      return;
    }

    applyShared();
  };

  const handleEffortChange = (next: string) => {
    if (next === reasoningEffort) {
      return;
    }

    applyIfConfirmed("effort", reasoningEffort ?? EMPTY, next, () => onEffortChange(next));
  };

  const handleTemperatureRelease = (next: number) => {
    if (next === temperature) {
      setDraggedTemperature(undefined);
      return;
    }

    applyIfConfirmed("temperature", isNil(temperature) ? EMPTY : temperature.toFixed(1), next.toFixed(1), () =>
      onTemperatureChange(next)
    ).then(() => setDraggedTemperature(undefined));
  };

  const shownTemperature = draggedTemperature ?? temperature;

  return (
    <div className={styles.settings}>
      <div className={styles.settingsGroup}>
        <div className={styles.modelSelect}>
          <DropdownInput
            mode="single"
            value={modelId}
            onChange={handleModelChange}
            placeholder="Model"
            width={200}
            options={map(models, ({ id, name, isDeprecated }) => ({
              value: id,
              label: isDeprecated ? `${name} (${MODEL_DEPRECATED_SUFFIX})` : name,
            }))}
          />
        </div>
        <Button type="link" className={styles.costButton} onClick={() => setIsCostVisible(true)}>
          {MODEL_COST_BUTTON}
        </Button>
      </div>

      {efforts.length > 0 && (
        <div className={styles.settingsGroup}>
          <span className={styles.settingsLabel}>{EFFORT_LABEL}</span>
          <div className={styles.effortSelect}>
            <DropdownInput
              mode="single"
              value={reasoningEffort}
              onChange={handleEffortChange}
              placeholder="Effort"
              width={140}
              options={map(efforts, (effort) => ({ value: effort, label: effort }))}
            />
          </div>
        </div>
      )}

      {showsTemperature && (
        <div className={styles.settingsGroup}>
          <span className={styles.settingsLabel}>{TEMPERATURE_LABEL}</span>
          <span className={styles.temperatureValue}>
            {isNil(shownTemperature) ? EMPTY : shownTemperature.toFixed(1)}
          </span>
          <span className={styles.temperatureEnd}>{TEMPERATURE_STRICT}</span>
          <div className={styles.temperatureSlider}>
            <Slider
              min={AICC_PROMPT_TEMPERATURE.min}
              max={AICC_PROMPT_TEMPERATURE.max}
              step={AICC_PROMPT_TEMPERATURE.step}
              value={shownTemperature ?? AICC_PROMPT_TEMPERATURE.min}
              onChange={setDraggedTemperature}
              onAfterChange={handleTemperatureRelease}
              tooltipVisible={false}
            />
          </div>
          <span className={styles.temperatureEnd}>{TEMPERATURE_CREATIVE}</span>
        </div>
      )}

      <ModelCostModal visible={isCostVisible} models={models} onClose={() => setIsCostVisible(false)} />
    </div>
  );
}

export default ModelSettings;
